import * as React from 'react'
import { useState } from 'react'
import { createPortal } from 'react-dom'
import axios from 'axios'
import { withTranslations, csrfToken } from 'helpers'
import { useTranslation } from 'hooks'
import Modal from '../../shared/Modal/Modal'
import UploadStep from './UploadStep'
import * as S from './ConsignmentRetrieval.styles'

interface UpdateLabelModalProps {
  userId: number
  retrievalId: number
  productTitle: string
  productImageUrl: string
  size: string
  styleCode: string
  showCancel?: boolean
  cancelUrl?: string
  cancelConfirm?: string
  cancelLabel?: string
}

const UpdateLabelModal: React.FC<UpdateLabelModalProps> = ({
  userId,
  retrievalId,
  productTitle,
  productImageUrl,
  size,
  styleCode,
  showCancel,
  cancelUrl,
  cancelConfirm,
  cancelLabel
}) => {
  const { t } = useTranslation()
  const [isCancelling, setIsCancelling] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const open = () => setIsOpen(true)
  const close = () => {
    setIsOpen(false)
    setTimeout(() => {
      setFile(null)
      setSubmitError(null)
    }, 200)
  }

  const handleSubmit = async () => {
    if (!file) return
    setIsSubmitting(true)
    setSubmitError(null)

    try {
      const formData = new FormData()
      formData.append('label_pdf', file)
      formData.append('authenticity_token', csrfToken)

      await axios.patch(
        `/api/v1/users/${userId}/consignment_retrievals/${retrievalId}`,
        formData
      )

      close()
      window.location.reload()
    } catch (err: any) {
      const message =
        err.response?.data?.error ||
        t('views.account.consignment_retrieval.submit_error')
      setSubmitError(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = async () => {
    if (!cancelUrl) return
    const msg = cancelConfirm || 'Are you sure?'
    if (!window.confirm(msg)) return

    setIsCancelling(true)
    try {
      await axios.delete(cancelUrl, {
        headers: { 'X-CSRF-Token': csrfToken }
      })
      window.location.reload()
    } catch {
      setIsCancelling(false)
    }
  }

  return (
    <>
      <S.RetrievalActions>
        <S.RetrievalActionBtn role="button" onClick={open}>
          {t('views.account.consignment_retrieval.update_label_button')}
        </S.RetrievalActionBtn>
        {showCancel && cancelUrl && (
          <S.RetrievalCancelBtn
            role="button"
            onClick={handleCancel}
            disabled={isCancelling}
          >
            {cancelLabel ||
              t('views.account.consignment_retrieval.cancel_retrieval')}
          </S.RetrievalCancelBtn>
        )}
      </S.RetrievalActions>

      {isOpen &&
        createPortal(
          <Modal toggleModal={close} drawer>
            <S.ModalContainer>
              <S.ModalHeader>
                <S.Title>
                  {t('views.account.consignment_retrieval.update_label_title')}
                </S.Title>
              </S.ModalHeader>

              <S.Body>
                <S.ProductCard>
                  <S.ProductImage src={productImageUrl} alt={productTitle} />
                  <S.ProductInfo>
                    <S.ProductTitle>{productTitle}</S.ProductTitle>
                    <S.ProductMeta>
                      {styleCode} &middot; {size}
                    </S.ProductMeta>
                  </S.ProductInfo>
                </S.ProductCard>

                <UploadStep file={file} onFileSelect={setFile} />
                {submitError && <S.ErrorText>{submitError}</S.ErrorText>}
              </S.Body>

              <S.Footer>
                <S.SecondaryBtn type="button" onClick={close}>
                  {t('shared.cancel')}
                </S.SecondaryBtn>
                <S.PrimaryBtn
                  type="button"
                  onClick={handleSubmit}
                  disabled={!file || isSubmitting}
                >
                  {isSubmitting
                    ? t('shared.loading')
                    : t(
                        'views.account.consignment_retrieval.update_label_confirm'
                      )}
                </S.PrimaryBtn>
              </S.Footer>
            </S.ModalContainer>
          </Modal>,
          document.body
        )}
    </>
  )
}

export default withTranslations(UpdateLabelModal)
